import { FC, useState, useEffect, useRef } from "react";
import {
  Stack,
  CircularProgress,
  Typography,
  IconButton,
  Button,
  Box,
  Card,
  CardContent,
  Collapse,
  Chip,
  LinearProgress,
  Tooltip,
  alpha,
  useMediaQuery,
  Theme,
} from "@mui/material";
import {
  Close,
  InsertDriveFile,
  CheckCircle,
  Warning,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { EditIcon } from "lucide-react";
import { formatFileSize } from "./utils";
import VideoPlayer from "./VideoPlayer";
import VideoThumbnail from "./VideoThumbnail";
import ThumbnailGenerator from "./ThumbnailGenerator";

export interface UploadFileCardProps {
  file: File;
  progress: number;
  status: "uploading" | "success" | "error";
  handleEditClick?: (file: File) => void;
  cancel: () => void;
}

export const UploadFileCard: FC<UploadFileCardProps> = ({
  file,
  progress,
  status,
  handleEditClick,
  cancel,
}) => {
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const fileRef = useRef<File | null>(null);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  useEffect(() => {
    const fileChanged =
      fileRef.current?.name !== file.name ||
      fileRef.current?.size !== file.size ||
      fileRef.current?.lastModified !== file.lastModified;

    if (fileChanged) {
      fileRef.current = file;
      setVideoDuration(0);

      if (videoSrc) {
        URL.revokeObjectURL(videoSrc);
        setVideoSrc(null);
      }

      if (thumbnail) {
        URL.revokeObjectURL(thumbnail);
        setThumbnail(null);
      }

      const url = URL.createObjectURL(file);
      setVideoSrc(url);
    }
  }, [file, videoSrc, thumbnail]);

  useEffect(() => {
    return () => {
      if (videoSrc) {
        URL.revokeObjectURL(videoSrc);
      }
      if (thumbnail) {
        URL.revokeObjectURL(thumbnail);
      }
    };
  }, [videoSrc, thumbnail]);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const handleVideoLoaded = (duration: number) => {
    setVideoDuration(duration);
  };

  const handleThumbnailGenerated = (thumbnailUrl: string) => {
    if (thumbnail) {
      URL.revokeObjectURL(thumbnail);
    }
    setThumbnail(thumbnailUrl);
  };

  const formattedDuration = videoDuration
    ? `${Math.floor(videoDuration / 60)}:${Math.floor(videoDuration % 60)
        .toString()
        .padStart(2, "0")}`
    : "0:00";

  const displayFileName = () => {
    if (!isMobile) return file.name;

    const maxLength = 20;
    if (file.name.length <= maxLength) return file.name;

    const extension =
      file.name.lastIndexOf(".") > 0
        ? file.name.substring(file.name.lastIndexOf("."))
        : "";

    let nameWithoutExt = extension
      ? file.name.substring(0, file.name.lastIndexOf("."))
      : file.name;

    if (nameWithoutExt.length + extension.length <= maxLength) {
      return file.name;
    }

    const truncatedName =
      nameWithoutExt.substring(0, maxLength - 3 - extension.length) +
      "..." +
      extension;
    return truncatedName;
  };

  return (
    <Card
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      variant="outlined"
      sx={{
        mb: 2,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 2,
          borderColor: (theme) => theme.palette.primary.light,
          cursor: "initial",
        },
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Stack spacing={1}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 1,
                  overflow: "hidden",
                  width: 48,
                  height: 48,
                  bgcolor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {thumbnail ? (
                  <Box
                    component="img"
                    src={thumbnail}
                    alt="Video thumbnail"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <InsertDriveFile sx={{ color: "text.secondary" }} />
                )}
                {status === "uploading" && progress !== 100 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha("#000", 0.5),
                    }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={progress}
                      size={30}
                      sx={{ color: "common.white" }}
                    />
                  </Box>
                )}
              </Box>

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  title={file.name}
                  sx={{
                    whiteSpace: isMobile ? "normal" : "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {displayFileName()}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(file.size)}
                  </Typography>
                  {videoDuration > 0 && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formattedDuration}
                      </Typography>
                    </>
                  )}
                  <Chip
                    label={
                      status === "uploading"
                        ? "Ready to Upload"
                        : status === "success"
                          ? "Success"
                          : "Error"
                    }
                    size="small"
                    color={
                      status === "error"
                        ? "error"
                        : status === "success"
                          ? "success"
                          : "default"
                    }
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              {videoSrc && (
                <Tooltip
                  title={expanded ? "Collapse preview" : "Expand preview"}
                >
                  <IconButton
                    size="small"
                    onClick={toggleExpand}
                    color="primary"
                  >
                    {expanded ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {status === "uploading" && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon size={16} />}
                  sx={{ whiteSpace: "nowrap", ml: 0.5 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick?.(file);
                  }}
                >
                  Edit
                </Button>
              )}

              {status === "uploading" && progress !== 100 && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    cancel();
                  }}
                  color="error"
                >
                  <Close fontSize="small" />
                </IconButton>
              )}

              {status === "success" && (
                <CheckCircle color="success" fontSize="small" />
              )}

              {status === "error" && <Warning color="error" fontSize="small" />}
            </Stack>
          </Stack>

          {/* Thumbnail generator - hidden component */}
          {videoSrc && (
            <ThumbnailGenerator
              videoSrc={videoSrc}
              onThumbnailGenerated={handleThumbnailGenerated}
            />
          )}

          <Collapse in={expanded} timeout="auto">
            <VideoPlayer
              videoSrc={videoSrc}
              thumbnail={thumbnail}
              isExpanded={expanded}
              onVideoLoaded={handleVideoLoaded}
              fileKey={`video-${file.name}-${file.lastModified}`}
            />
          </Collapse>

          {!expanded && videoSrc && (
            <VideoThumbnail
              thumbnail={thumbnail}
              duration={formattedDuration}
              onClick={toggleExpand}
            />
          )}
        </Stack>
      </CardContent>

      {status === "uploading" && progress < 100 && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 4 }}
        />
      )}
    </Card>
  );
};

export default UploadFileCard;
